<script lang="ts">
	import { format } from 'src/services/i18n';
	import { locale } from 'svelte-i18n';
	import DocumentContextualNavigation from 'src/components/DocumentContextualNavigation.svelte';
	import { CHANGE_DATE } from 'src/constants';
	import { localizedLastUpdate } from 'src/helpers/localized_last_update_helper';

	$: menuItems = [
		{
			id: 'data-information-notice',
			label: $format('text_data_act_header_data_information_notice', {
				default: 'Data Information Notice',
			}),
			active: true,
		},
		{
			id: 'transferred-product-data',
			label: $format('text_data_act_header_transferred_product_data', {
				default: 'Transferred Product Data',
			}),
		},
		{
			id: 'related-service-data',
			label: $format('text_data_act_header_related_service_data', {
				default: 'Related Service Data',
			}),
		},
		{
			id: 'use-of-data',
			label: $format('text_data_act_header_use_of_data', {
				default: 'Use of Data by the Data Holder & Third Parties',
			}),
		},
		{
			id: 'identity-data-holder',
			label: $format('text_data_act_header_identity_data_holder', {
				default: 'Identity of the Data Holder',
			}),
		},
		{
			id: 'communication',
			label: $format('text_data_act_header_communication', {
				default: 'Means of Communication',
			}),
		},
		{
			id: 'data-sharing-control',
			label: $format('text_data_act_header_data_sharing_control', {
				default: 'Data Sharing with Third Parties – User Control',
			}),
		},
		{
			id: 'complaint-rights',
			label: $format('text_data_act_header_complaint_rights', {
				default: 'Right to lodge a complaint',
			}),
		},
		{
			id: 'trade-secrets',
			label: $format('text_data_act_header_trade_secrets', {
				default: 'Trade Secret',
			}),
		},
		{
			id: 'service-contract',
			label: $format('text_data_act_header_service_contract', {
				default: 'Duration of the Service contract',
			}),
		},
	];

	$: sectionsContent = [
		{
			id: 'data-information-notice',
			title: $format('text_data_act_header_data_information_notice_content', {
				default:
					"Data Information Notice in accordance with Art. 3(3) of Regulation (EU) 2023/2854 ('Data Act') for related services",
			}),
			titleDesc: [
				$format('text_data_act_data_information_notice_desc_1', {
					default: 'Shelly Smart Control („Service")',
				}),
				$format('text_data_act_data_information_notice_desc_2', {
					default:
						'Shelly Device ("the Device" / "connected product") can be used together with the Shelly Smart Control Service provided by Shelly Europe Ltd.',
				}),
				$format('text_data_act_data_information_notice_desc_3', {
					default:
						'In the following, we as Shelly Europe Ltd. (hereinafter "Shelly" or "we" or "us") inform you in accordance with Art. 3 Para. 3 Data Act (Regulation (EU) 2023/2854) about the product data that is generated through the use of a Shelly Device with the Shelly cloud-based connected service Shelly Smart Control available in mobile and web version ("the Service").',
				}),
				$format('text_data_act_data_information_notice_desc_4', {
					default:
						'The use of the Shelly Device within the Shelly Smart Control system is at discretion of the user who might also decide to use the connected product only locally via its embedded web UI, which provides direct control and configuration without needing the cloud or an internet connection.',
				}),
				$format('text_data_act_data_information_notice_desc_5', {
					default:
						'When a Shelly Device is connected to Shelly Smart Control, the connected product generates and transmits various data and information during its use. The following provides information about the data transferred and made available when connecting the Device with the Service, how to access this data, and your data rights.',
				}),
			],
		},
		{
			id: 'transferred-product-data',
			title: $format('text_data_act_header_transferred_product_data', {
				default: 'Transferred Product Data',
			}),
			subtitles: [
				{
					title: $format('text_data_act_transferred_product_data_nature_title', {
						default: 'Nature of Product Data:',
					}),
					description: [
						$format('text_data_act_transferred_product_data_nature_desc', {
							default:
								'Shelly Smart Control collects and process operational data generated and transferred by the use of Shelly devices, such as switching status, power consumption, voltage, temperature readings, schedules, and device identifiers (e.g., device ID, MAC address, firmware and app version), error messages, setting values / user configurations.',
						}),
					],
				},
				{
					title: $format('text_data_act_transferred_product_data_volume_title', {
						default: 'Estimated volume:',
					}),
					description: [
						$format('text_data_act_transferred_product_data_volume_desc', {
							default:
								'Depending on the number of connected devices, each device typically generates small telemetry packets at regular intervals (for example, 1–10 KB per measurement).',
						}),
					],
				},
				{
					title: $format('text_data_act_transferred_product_data_frequency_title', {
						default: 'Collection frequency:',
					}),
					description: [
						$format('text_data_act_transferred_product_data_frequency_desc', {
							default:
								'The product data is transmitted continuously as long as an Internet connection exists. Data is collected at event triggers (e.g., device turned on/off) and at predefined reporting intervals (commonly every few seconds to minutes, configurable by the user).',
						}),
					],
				},
				{
					title: $format('text_data_act_transferred_product_data_access_title', {
						default: 'User access/retrieval:',
					}),
					description: [
						$format('text_data_act_transferred_product_data_access_desc', {
							default:
								'Data is directly accessible via the related Service Shelly Smart Control (available as mobile or web version) and devices local Web UI. The user can retrieve historical data through specifically dedicated UI in the Service mobile or web version. Data export in CSV format is available where applicable via the same UI, or via APIs.',
						}),
					],
				},
				{
					title: $format('text_data_act_transferred_product_data_storage_title', {
						default: 'Data storage arrangements:',
					}),
					description: [
						$format('text_data_act_transferred_product_data_storage_desc', {
							default: 'Data is stored in secure cloud servers located in the EU/EEA.',
						}),
					],
				},
				{
					title: $format('text_data_act_transferred_product_data_retention_title', {
						default: 'Retention period:',
					}),
					description: [
						$format('text_data_act_transferred_product_data_retention_desc', {
							default:
								'Historically transferred device-generated data is typically retained for as long as the account is active, with possible limits (e.g., 6–24 months of detailed logs, and different retention and aggregated strategies for different types of statistics).',
						}),
					],
				},
			],
		},
		{
			id: 'related-service-data',
			title: $format('text_data_act_header_related_service_data', {
				default: 'Related Service Data',
			}),
			titleDesc: [
				$format('text_data_act_related_service_data_desc_1', {
					default:
						'In addition to transferred product data, related service data is generated when using the service.',
				}),
			],
			subtitles: [
				{
					title: $format('text_data_act_related_service_data_nature_title', {
						default: 'Nature of service data:',
					}),
					description: [
						$format('text_data_act_related_service_data_nature_desc', {
							default:
								'Includes account information (user ID, email, authentication logs, content), app usage data, error logs, support interactions, and configuration preferences, additionally calculated values, historical data and statistics.',
						}),
					],
				},
				{
					title: $format('text_data_act_related_service_data_volume_title', {
						default: 'Estimated volume:',
					}),
					description: [
						$format('text_data_act_related_service_data_volume_desc', {
							default:
								'Data is generated during use and operation in real time and continuously. The amount of data varies depending on the data point and the use of the Service, with data volumes depending only on user activity ranging from 0 Kbyte/h when no user activity up to 10 MByte/h when user interacts actively with the application, adding multiple devices and pictures into the Service.',
						}),
					],
				},
				{
					title: $format('text_data_act_related_service_data_access_title', {
						default: 'User access/retrieval:',
					}),
					description: [
						$format('text_data_act_related_service_data_access_desc', {
							default:
								'Data generated and potentially stored within the related service are accessible via Shelly Smart Control Service (mobile and web version) available for iOS and Android and at https://control.shelly.cloud/ , via APIs available at https://shelly-api-docs.shelly.cloud/ or subject access requests.',
						}),
					],
				},
				{
					title: $format('text_data_act_related_service_data_storage_title', {
						default: 'Data storage arrangements:',
					}),
					description: [
						$format('text_data_act_related_service_data_storage_desc', {
							default:
								"Stored in Shelly's secure EU/EEA data centers with backup and disaster-recovery measures.",
						}),
					],
				},
				{
					title: $format('text_data_act_related_service_data_retention_title', {
						default: 'Retention period:',
					}),
					description: [
						$format('text_data_act_related_service_data_retention_desc', {
							default:
								'Service data is retained for the duration of the contractual relationship and up to statutory limitation periods. We generally store Product Data for as long as it is necessary for the functionality of the Shelly Device with the Service for the provision of the Service itself and its functionalities, for the provision of an associated service (e.g. support service) or as long as there is a legally recognized interest in further storage (e.g. product improvement, statutory retention periods).',
						}),
					],
				},
				{
					title: $format('text_data_act_related_service_data_terms_title', {
						default: 'Terms of Service:',
					}),
					description: [
						$format('text_data_act_related_service_data_terms_desc', {
							default:
								'The Service is provided under General Terms and Conditions to be accepted by the user upon registering for the Service and as updated from time to time available at: https://control.shelly.cloud/#/terms-and-conditions',
						}),
					],
				},
			],
		},
		{
			id: 'use-of-data',
			title: $format('text_data_act_header_use_of_data', {
				default: 'Use of Data by the Data Holder & Third Parties',
			}),
			subtitles: [
				{
					title: $format('text_data_act_use_of_data_internal_title', {
						default: 'Internal use:',
					}),
					description: [
						$format('text_data_act_use_of_data_internal_desc', {
							default:
								'Shelly Europe Ltd. may use readily available Device and Service Data to provide the functionalities of the Service and the connected product, for further service and product development, to improve product and service performance, detect malfunctions, enhance user experience, and ensure cybersecurity. Aggregated/anonymized data may be used for analytics, R&D, capacity planning and AI model development.',
						}),
					],
				},
				{
					title: $format('text_data_act_use_of_data_third_party_title', {
						default: 'Third-party use:',
					}),
					description: [
						$format('text_data_act_use_of_data_third_party_desc', {
							default:
								'Data may be shared with or transferred to third parties (integration partners like smart home platforms) as technically feasible and strictly for purposes agreed upon with the user (e.g., linking Shelly devices with Amazon Alexa, Google Home, or Home Assistant). Explicit user consent is required for onward sharing. Upon sharing / transferring data the terms of data processing and use of the referent third party service shall apply. The user is solely responsible for sharing or subsequently transmitting the exported data to the intended third parties.',
						}),
					],
				},
			],
		},
		{
			id: 'identity-data-holder',
			title: $format('text_data_act_header_identity_data_holder', {
				default: 'Identity of the Data Holder',
			}),
			titleDesc: [
				$format('text_data_act_identity_data_holder_desc_1', {
					default: 'Trading name: Shelly Europe Ltd.',
				}),
				$format('text_data_act_identity_data_holder_desc_2', {
					default: 'Establishment: Sofia, Bulgaria',
				}),
				$format('text_data_act_identity_data_holder_desc_3', {
					default: 'Geographical address: 51 Cherni Vrah, office building 3, floor 2 and 3',
				}),
				$format('text_data_act_identity_data_holder_desc_4', {
					default:
						'Other processors: Data processors, as disclosed in Shelly Smart Control Privacy Policy, may process data on behalf of Shelly.',
				}),
			],
		},
		{
			id: 'communication',
			title: $format('text_data_act_header_communication', {
				default: 'Means of Communication',
			}),
			titleDesc: [
				$format('text_data_act_communication_desc_1', {
					default: 'Contact channels:',
				}),
				$format('text_data_act_communication_desc_2', {
					default: 'Dedicated support portal: https://support.shelly.cloud/en/support/home',
				}),
				$format('text_data_act_communication_desc_3', {
					default: 'Email: support@shelly.com',
				}),
				$format('text_data_act_communication_desc_4', {
					default:
						'Contact form and live chat via the official website https://www.shelly.com',
				}),
				$format('text_data_act_communication_desc_5', {
					default:
						'These ensure users can contact Shelly quickly and receive efficient communication.',
				}),
			],
		},
		{
			id: 'data-sharing-control',
			title: $format('text_data_act_header_data_sharing_control', {
				default: 'Data Sharing with Third Parties – User Control',
			}),
			subtitles: [
				{
					title: $format('text_data_act_data_sharing_control_request_title', {
						default: 'Request procedure:',
					}),
					description: [
						$format('text_data_act_data_sharing_control_request_desc', {
							default:
								'Users can initiate sharing through the Shelly Smart Control Service or via integrations (e.g., enabling third-party smart home connectors).',
						}),
					],
				},
				{
					title: $format('text_data_act_data_sharing_control_withdrawal_title', {
						default: 'Withdrawal procedure:',
					}),
					description: [
						$format('text_data_act_data_sharing_control_withdrawal_desc', {
							default:
								"Sharing can be ended by disabling integrations within the Service, revoking API tokens, or by submitting a request to Shelly's support team.",
						}),
					],
				},
				{
					title: $format('text_data_act_data_sharing_control_legal_title', {
						default: 'Legal framework:',
					}),
					description: [
						$format('text_data_act_data_sharing_control_legal_desc', {
							default:
								'All sharing and withdrawal requests are handled in compliance with GDPR and the EU Data Act, ensuring users retain full control over their data.',
						}),
					],
				},
			],
		},
		{
			id: 'complaint-rights',
			title: $format('text_data_act_header_complaint_rights', {
				default: 'Right to lodge a complaint',
			}),
			titleDesc: [
				$format('text_data_act_complaint_rights_desc', {
					default:
						"The user is entitled to lodge a complained alleging an infringement of any of user's rights under the EU Data Act with the competent authority designated to supervise the compliance of the service;",
				}),
			],
		},
		{
			id: 'trade-secrets',
			title: $format('text_data_act_header_trade_secrets', {
				default: 'Trade Secret',
			}),
			titleDesc: [
				$format('text_data_act_trade_secrets_desc_1', {
					default:
						'The data generated by the connected product and the Service may include data that is subject to trade secret protection;',
				}),
				$format('text_data_act_trade_secrets_desc_2', {
					default:
						'All data points starting with /statistics/ or /v2/statistics/ contain trade secrets related to highly enriched statistical data and are protected accordingly.',
				}),
				$format('text_data_act_trade_secrets_desc_3', {
					default:
						'All data points starting with /scene or /integrations or /v2/integrations or /v2/scenes contain trade secrets related to internal organization, operation and functioning of the Service and its features and are protected accordingly.',
				}),
				$format('text_data_act_trade_secrets_desc_4', {
					default:
						'All statistical datasets, analytical models, and related files provided through AI services constitute trade secrets and are protected accordingly.',
				}),
				$format('text_data_act_trade_secrets_desc_5', {
					default:
						'All APIs related to access to device generated data, covered by Data Act, are documented at https://shelly-api-docs.shelly.cloud All other existing APIs not documented might contain tradе secrets and should be considered protected accordingly.',
				}),
			],
		},
		{
			id: 'service-contract',
			title: $format('text_data_act_header_service_contract', {
				default: 'Duration of the Service contract',
			}),
			titleDesc: [
				$format('text_data_act_service_contract_desc', {
					default:
						'The Service is provided based on a contractual relationship between the Data Holder and the User concluded under General Terms and Conditions upon the registration of an account with the Service. The user is entitled to terminate this contract at any time upon deletion of the account with the Service.',
				}),
			],
		},
	];

	$: latestVersion = localizedLastUpdate(
		CHANGE_DATE.DA * 1000,
		$locale,
		'data_act_last_update',
		$format,
	);

	$: title = $format('data_act', { default: 'EU Data act' });
</script>

<DocumentContextualNavigation {title} {menuItems} {sectionsContent} {latestVersion} />
